import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const ADMIN_TOKEN_KEY = 'ip_admin_token';

const adminApi = axios.create({ baseURL: BASE });

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let _loggingOut = false;

adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const errCode = String(err.response?.data?.error || '').toLowerCase();

    // Only logout when the token itself is invalid/expired — not on every 401
    // (e.g. a 401 from a specific resource check should not kill the whole session)
    if (status === 401 && !_loggingOut) {
      const isAuthEndpoint = err.config?.url?.includes('/admin/auth/');
      const isTokenError   = errCode.includes('token') ||
                             errCode.includes('expired') ||
                             errCode.includes('invalid admin') ||
                             errCode.includes('no token');

      if (isAuthEndpoint || isTokenError) {
        _loggingOut = true;
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        window.location.href = '/admin/login';
      }
    }
    // 429: rate limited — surface the error, don't logout
    return Promise.reject(err);
  }
);

// ── Dashboard ─────────────────────────────────────────────────────────────────
export const getDashboardStats  = () => adminApi.get('/admin/stats');

// ── Job Roles ─────────────────────────────────────────────────────────────────
export const getRoles            = () => adminApi.get('/admin/roles');
export const createRole          = (data) => adminApi.post('/admin/roles', data);
export const updateRole          = (id, data) => adminApi.put(`/admin/roles/${id}`, data);
export const deleteRole          = (id) => adminApi.delete(`/admin/roles/${id}`);
export const toggleRoleActive    = (id) => adminApi.patch(`/admin/roles/${id}/toggle`);

// ── Chapters ──────────────────────────────────────────────────────────────────
export const getChapters         = (roleId) => adminApi.get(`/admin/roles/${roleId}/chapters`);
export const createChapter       = (roleId, data) => adminApi.post(`/admin/roles/${roleId}/chapters`, data);
export const updateChapter       = (id, data) => adminApi.put(`/admin/chapters/${id}`, data);
export const deleteChapter       = (id) => adminApi.delete(`/admin/chapters/${id}`);

// ── Sub-chapters ──────────────────────────────────────────────────────────────
export const getSubChapters      = (chapterId) => adminApi.get(`/admin/chapters/${chapterId}/sub-chapters`);
export const createSubChapter    = (chapterId, data) => adminApi.post(`/admin/chapters/${chapterId}/sub-chapters`, data);
export const updateSubChapter    = (id, data) => adminApi.put(`/admin/sub-chapters/${id}`, data);
export const deleteSubChapter    = (id) => adminApi.delete(`/admin/sub-chapters/${id}`);

// ── Questions ─────────────────────────────────────────────────────────────────
export const getQuestions        = (subChapterId) => adminApi.get(`/admin/sub-chapters/${subChapterId}/questions`);
export const generateQuestions   = (subChapterId) => adminApi.post(`/admin/sub-chapters/${subChapterId}/generate`);
export const approveQuestion     = (id) => adminApi.patch(`/admin/questions/${id}/approve`);
export const updateQuestion      = (id, data) => adminApi.put(`/admin/questions/${id}`, data);
export const deleteQuestion      = (id) => adminApi.delete(`/admin/questions/${id}`);
export const bulkApprove         = (ids) => adminApi.post('/admin/questions/bulk-approve', { ids });

// ── Dashboard ─────────────────────────────────────────────────────────────────
export const getRecentActivity   = () => adminApi.get('/admin/activity');

// ── Users ─────────────────────────────────────────────────────────────────────
export const getUsers            = (params) => adminApi.get('/admin/users', { params });
export const getUser             = (id) => adminApi.get(`/admin/users/${id}`);
export const grantAccess         = (userId, roleId) => adminApi.post('/admin/users/grant-access', { userId, roleId });
export const revokeAccess        = (subscriptionId) => adminApi.delete(`/admin/subscriptions/${subscriptionId}/revoke`);
export const suspendUser         = (id, reason) => adminApi.patch(`/admin/users/${id}/suspend`, { reason });
export const unsuspendUser       = (id) => adminApi.patch(`/admin/users/${id}/unsuspend`);
export const getUserNotes        = (id) => adminApi.get(`/admin/users/${id}/notes`);
export const addUserNote         = (id, note) => adminApi.post(`/admin/users/${id}/notes`, { note });
export const deleteUserNote      = (noteId) => adminApi.delete(`/admin/users/notes/${noteId}`);

// ── Subscriptions ─────────────────────────────────────────────────────────────
export const getSubscriptions    = (params) => adminApi.get('/admin/subscriptions', { params });
export const getSubsSummary      = () => adminApi.get('/admin/subscriptions/summary');
export const extendSubscription  = (id, days) => adminApi.patch(`/admin/subscriptions/${id}/extend`, { days });
export const getPayments         = (params) => adminApi.get('/admin/payments', { params });
export const issueRefund         = (paymentId, amount) => adminApi.post(`/admin/payments/${paymentId}/refund`, { amount });

// ── Analytics ─────────────────────────────────────────────────────────────────
export const getUserAnalytics    = () => adminApi.get('/admin/analytics/users');
export const getRevenueAnalytics = () => adminApi.get('/admin/analytics/revenue');
export const getContentAnalytics = () => adminApi.get('/admin/analytics/content');
export const getConversionData   = () => adminApi.get('/admin/analytics/conversion');
export const getTrialAnalytics   = () => adminApi.get('/admin/analytics/trial');

// ── Certificates ──────────────────────────────────────────────────────────────
export const getCertificates     = (params) => adminApi.get('/admin/certificates', { params });
export const revokeCertificate   = (id)     => adminApi.patch(`/admin/certificates/${id}/revoke`);
export const deleteCertificate   = (id)     => adminApi.delete(`/admin/certificates/${id}`);

// ── Admin users ───────────────────────────────────────────────────────────────
export const getAdmins           = () => adminApi.get('/admin/admins');
export const createAdmin         = (data) => adminApi.post('/admin/admins', data);
export const updateAdmin         = (id, data) => adminApi.put(`/admin/admins/${id}`, data);
export const resetAdminPassword  = (id, password) => adminApi.patch(`/admin/admins/${id}/reset-password`, { password });
export const deleteAdmin         = (id) => adminApi.delete(`/admin/admins/${id}`);

// ── Settings ──────────────────────────────────────────────────────────────────
export const getSettings         = () => adminApi.get('/admin/settings');
export const updateSettings      = (data) => adminApi.put('/admin/settings', data);

// ── Audit log ─────────────────────────────────────────────────────────────────
export const getActivityLog      = (params) => adminApi.get('/admin/activity-log', { params });

// ── Content health ────────────────────────────────────────────────────────────
export const getContentHealth    = () => adminApi.get('/admin/content/health');

// ── Question review queue ─────────────────────────────────────────────────────
export const getReviewQueue      = (params) => adminApi.get('/admin/questions/review-queue', { params });
export const rejectQuestion      = (id) => adminApi.patch(`/admin/questions/${id}/reject`);

// ── At-risk users ─────────────────────────────────────────────────────────────
export const getAtRiskUsers      = () => adminApi.get('/admin/users/at-risk');

// ── Global search ─────────────────────────────────────────────────────────────
export const globalSearch        = (q) => adminApi.get('/admin/search', { params: { q } });

// ── Impersonate ───────────────────────────────────────────────────────────────
export const impersonateUser     = (id) => adminApi.post(`/admin/users/${id}/impersonate`);

// ── Coupons ───────────────────────────────────────────────────────────────────
export const getCoupons          = () => adminApi.get('/admin/coupons');
export const createCoupon        = (data) => adminApi.post('/admin/coupons', data);
export const toggleCoupon        = (id) => adminApi.patch(`/admin/coupons/${id}/toggle`);
export const deleteCoupon        = (id) => adminApi.delete(`/admin/coupons/${id}`);

// ── Broadcast email ───────────────────────────────────────────────────────────
export const broadcastEmail      = (data) => adminApi.post('/admin/broadcast-email', data);

// ── Email Templates ───────────────────────────────────────────────────────────
export const getEmailTemplates   = ()           => adminApi.get('/admin/email-templates');
export const getEmailTemplate    = (key)        => adminApi.get(`/admin/email-templates/${key}`);
export const updateEmailTemplate = (key, data)  => adminApi.put(`/admin/email-templates/${key}`, data);

// ── JD Prep ───────────────────────────────────────────────────────────────────
export const getJdPrepStats      = () => adminApi.get('/admin/jd-prep/stats');

// ── Support inbox ─────────────────────────────────────────────────────────────
export const getSupportTickets   = (status) => adminApi.get('/admin/support', { params: { status } });
export const updateSupportTicket = (id, data) => adminApi.patch(`/admin/support/${id}`, data);
export const deleteSupportTicket = (id) => adminApi.delete(`/admin/support/${id}`);

// ── Blog CMS ──────────────────────────────────────────────────────────────────
export const getBlogPosts           = ()            => adminApi.get('/admin/blog');
export const getBlogPost            = (id)          => adminApi.get(`/admin/blog/${id}`);
export const createBlogPost         = (data)        => adminApi.post('/admin/blog', data);
export const updateBlogPost         = (id, data)    => adminApi.put(`/admin/blog/${id}`, data);
export const deleteBlogPost         = (id)          => adminApi.delete(`/admin/blog/${id}`);
export const previewBlogGenerate    = (titles)      => adminApi.post('/admin/blog/generate/preview', { titles });
export const submitBlogGenerateBatch = (outlines)   => adminApi.post('/admin/blog/generate/batch', { outlines });
export const getBlogGenerateJob     = (jobId)       => adminApi.get(`/admin/blog/jobs/${jobId}`);
export const regenerateBlogContent  = (id)          => adminApi.post(`/admin/blog/${id}/generate-content`);

// ── LinkedIn Carousel Generator ────────────────────────────────────────────────
export const generateCarousel   = (data)  => adminApi.post('/admin/carousel/generate', data);
export const getCarouselHistory = ()      => adminApi.get('/admin/carousel/history');
export const getCarousel        = (id)    => adminApi.get(`/admin/carousel/${id}`);
export const downloadCarouselPdf = (id)   => adminApi.get(`/admin/carousel/${id}/pdf`, { responseType: 'blob' });
export const deleteCarousel     = (id)    => adminApi.delete(`/admin/carousel/${id}`);
export const getTrendingTopics  = (refresh, category) => adminApi.get('/admin/carousel/trending', {
  params: { ...(refresh ? { refresh: '1' } : {}), ...(category ? { category } : {}) },
});
export const swapCarouselHook   = (id, hookIndex) => adminApi.patch(`/admin/carousel/${id}/hook`, { hookIndex });
export const updateCarouselStats = (id, stats)    => adminApi.patch(`/admin/carousel/${id}/stats`, stats);
export const postCarouselToLinkedIn = (id)        => adminApi.post(`/admin/carousel/${id}/post-linkedin`);

// ── LinkedIn connection ──────────────────────────────────────────────────────
export const getLinkedInStatus     = ()                  => adminApi.get('/admin/linkedin/status');
export const connectLinkedIn       = (organizationUrn)   => adminApi.post('/admin/linkedin/connect', { organizationUrn });
export const disconnectLinkedIn    = ()                  => adminApi.delete('/admin/linkedin/disconnect');

// ── Role pricing ──────────────────────────────────────────────────────────────
export const updateRolePricing   = (id, data) => adminApi.patch(`/admin/roles/${id}/pricing`, data);

export default adminApi;

// ── Bulk upload ───────────────────────────────────────────────────────────────
export const downloadTemplate = () => adminApi.get('/bulk/template', { responseType: 'blob' });
export const bulkUpload        = (formData) => adminApi.post('/bulk/upload', formData);

// ── Question generation (per-role) ────────────────────────────────────────────
export const getQuestionStatus    = (roleId)  => adminApi.get(`/admin/roles/${roleId}/question-status`);
export const startGeneration      = (roleId)  => adminApi.post(`/admin/roles/${roleId}/generate-questions`);
export const getGenerationJob     = (roleId)  => adminApi.get(`/admin/roles/${roleId}/generation-job`);
export const clearGenerationJob   = (roleId)  => adminApi.delete(`/admin/roles/${roleId}/generation-job`);

// ── Question generation (all roles) ──────────────────────────────────────────
export const startGenerationAll   = (role_ids) => adminApi.post('/admin/generate-all', role_ids?.length ? { role_ids } : {});
export const getGenerationAllJob  = ()         => adminApi.get('/admin/generate-all/status');
export const stopGenerationAll    = ()         => adminApi.delete('/admin/generate-all');
export const getQuestionAudit     = ()         => adminApi.get('/admin/question-audit');

// ── Study content generation ──────────────────────────────────────────────────
export const getStudyContentList    = ()       => adminApi.get('/admin/study-content');
export const startStudyGeneration   = (ids)    => adminApi.post('/admin/study-content/generate', { sub_chapter_ids: ids });
export const getStudyGenerationJob  = (jobId)  => adminApi.get(`/admin/study-content/job/${jobId}`);
export const deleteStudyContent     = (scId)   => adminApi.delete(`/admin/study-content/${scId}`);

// ── Explain content generation ────────────────────────────────────────────────
export const getExplainContentList    = ()          => adminApi.get('/admin/explain-content');
export const startExplainGeneration   = (role_ids)  => adminApi.post('/admin/explain-content/generate', { role_ids });
export const getExplainGenerationJob  = (jobId)     => adminApi.get(`/admin/explain-content/job/${jobId}`);
export const clearExplainContent      = (roleId)    => adminApi.delete(`/admin/explain-content/${roleId}`);

// ── Export ────────────────────────────────────────────────────────────────────
export const exportAll    = ()         => adminApi.get('/admin/export', { responseType: 'blob' });
export const exportRole   = (roleId)   => adminApi.get(`/admin/export?role_id=${roleId}`, { responseType: 'blob' });

// ── B2B Companies ─────────────────────────────────────────────────────────────
export const getAdminCompanies      = ()          => adminApi.get('/admin/companies');
export const getAdminCompany        = (id)        => adminApi.get(`/admin/companies/${id}`);
export const toggleCompanyActivation = (id, activate, grantData = {}) => adminApi.patch(`/admin/companies/${id}/activate`, { activate, ...grantData });
export const toggleCompanyWhitelabel = (id, enabled) => adminApi.patch(`/admin/companies/${id}/whitelabel`, { enabled });


// ── Company Coupons ───────────────────────────────────────────────────────────
export const getCompanyCoupons    = ()    => adminApi.get('/admin/company-coupons');
export const createCompanyCoupon  = (d)   => adminApi.post('/admin/company-coupons', d);
export const deleteCompanyCoupon  = (id)  => adminApi.delete(`/admin/company-coupons/${id}`);

// ── Courses ───────────────────────────────────────────────────────────────────
export const getAdminCourses        = ()         => adminApi.get('/admin/courses');
export const getAdminCourse         = (id)       => adminApi.get(`/admin/courses/${id}`);
export const updateAdminCourse      = (id, data) => adminApi.patch(`/admin/courses/${id}`, data);
export const updateAdminCourseStatus = (id, status) => adminApi.patch(`/admin/courses/${id}/status`, { status });
export const deleteAdminCourse      = (id)       => adminApi.delete(`/admin/courses/${id}`);
export const generateCourseOutline  = (data)     => adminApi.post('/admin/courses/outline', data);
export const generateCourse         = (data)     => adminApi.post('/admin/courses/generate', data);
export const getCourseJob           = (jobId)    => adminApi.get(`/admin/courses/jobs/${jobId}`);
export const retryCourseBatch       = (courseId, domainType) =>
  adminApi.post(`/admin/courses/${courseId}/retry-batch`, { domain_type: domainType });
export const resumeCourseBatch      = (courseId) =>
  adminApi.get(`/admin/courses/${courseId}/batch-resume`);
export const regenerateTopic        = (topicId, domainType) =>
  adminApi.post(`/admin/courses/topics/${topicId}/regenerate`, { domain_type: domainType });
export const updateTopicStatus      = (topicId, status) =>
  adminApi.patch(`/admin/courses/topics/${topicId}/status`, { status });

// ── Vouchers ──────────────────────────────────────────────────────────────────
export const getAdminVouchers       = ()         => adminApi.get('/wallet/admin/vouchers');
export const createAdminVoucher     = (data)     => adminApi.post('/wallet/admin/vouchers', data);
export const bulkCreateVouchers     = (data)     => adminApi.post('/wallet/admin/vouchers/bulk', data);
export const updateAdminVoucher     = (id, data) => adminApi.patch(`/wallet/admin/vouchers/${id}`, data);
export const deleteAdminVoucher     = (id)       => adminApi.delete(`/wallet/admin/vouchers/${id}`);

// ── Withdrawals ───────────────────────────────────────────────────────────────
export const getAdminWithdrawals    = (status)   => adminApi.get('/wallet/admin/withdrawals', { params: { status } });
export const processWithdrawal      = (id, data) => adminApi.patch(`/wallet/admin/withdrawals/${id}`, data);
